// v2.2 findings #76 item 2 and the Crew Mode Field Bundle PRD (companion web
// changes): per-unit / per-facility settings kept in `metadata` (no schema
// migration).
//
// - `metadata.status === 'inactive'` → "Inactiva": the unit / facility keeps
//   all its records and history, but is no longer offered for new captures
//   (Crew Mode pickers) and cannot be deleted while it holds records.
// - `metadata.crewOrder` (number) → position in Crew Mode's location list.
// - `metadata.crewHidden === true` → never shown in Crew Mode.

type WithMetadata = { metadata?: Record<string, unknown> | null } | null | undefined

const readMetadata = (value: WithMetadata): Record<string, unknown> => {
  const metadata = value?.metadata
  return metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {}
}

export const isInactive = (value: WithMetadata): boolean => readMetadata(value).status === 'inactive'

export const isHiddenFromCrew = (value: WithMetadata): boolean => readMetadata(value).crewHidden === true

export const crewOrderOf = (value: WithMetadata): number | null => {
  const order = readMetadata(value).crewOrder
  return typeof order === 'number' && Number.isFinite(order) ? order : null
}

/** Units in the office-defined Crew Mode order; units without a position follow, by name. */
export const sortUnitsForCrew = <T extends { name?: string } & NonNullable<WithMetadata>>(units: T[]): T[] =>
  [...units].sort((a, b) => {
    const orderA = crewOrderOf(a)
    const orderB = crewOrderOf(b)
    if (orderA !== null && orderB !== null && orderA !== orderB) return orderA - orderB
    if (orderA !== null && orderB === null) return -1
    if (orderA === null && orderB !== null) return 1
    return String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { numeric: true })
  })

/**
 * Findings #79 follow-up (#82): web capture screens keep Inactiva units
 * selectable (their history stays viewable) but list them last, marked, and
 * block new entries on them.
 */
export const buildUnitSelectOptions = (
  units: ReadonlyArray<{ id?: unknown; name?: unknown; metadata?: Record<string, unknown> | null }>,
  inactiveSuffix: string
): Array<{ key: string; value: string; label: string }> => {
  const toOption = (unit: { id?: unknown; name?: unknown; metadata?: Record<string, unknown> | null }) => {
    const value = String(unit.id ?? unit.name ?? '')
    const name = String(unit.name ?? value)
    return { key: value, value, label: isInactive(unit) ? `${name} ${inactiveSuffix}` : name }
  }
  return [
    ...units.filter((unit) => !isInactive(unit)).map(toOption),
    ...units.filter((unit) => isInactive(unit)).map(toOption)
  ]
}

export const isUnitIdInactive = (
  units: ReadonlyArray<{ id?: unknown; name?: unknown; metadata?: Record<string, unknown> | null }>,
  productionUnitId: string
): boolean => {
  if (!productionUnitId) return false
  const unit = units.find((candidate) => String(candidate.id ?? candidate.name ?? '') === String(productionUnitId))
  return isInactive(unit)
}

export const withStatus = <T extends NonNullable<WithMetadata>>(value: T, inactive: boolean): T => {
  const metadata = { ...readMetadata(value) }
  if (inactive) metadata.status = 'inactive'
  else delete metadata.status
  return { ...value, metadata }
}

export const withCrewSettings = <T extends NonNullable<WithMetadata>>(
  value: T,
  settings: { crewOrder?: number | null; crewHidden?: boolean }
): T => {
  const metadata = { ...readMetadata(value) }
  if (settings.crewOrder === null) delete metadata.crewOrder
  else if (typeof settings.crewOrder === 'number') metadata.crewOrder = settings.crewOrder
  if (settings.crewHidden === true) metadata.crewHidden = true
  else if (settings.crewHidden === false) delete metadata.crewHidden
  return { ...value, metadata }
}
